// A 401 only means "this session is over" when the request actually carried the token the app
// holds NOW. Access tokens get rotated underneath in-flight requests:
//
//   - confirming 2FA stamps JWTInvalidationDate on the user, which kills every token issued
//     before that moment, and answers with a fresh pair the app swaps in
//     (handle-confirm-two-factor-auth.js / EditorUserSecurityTwoFactorConfirmation.vue)
//   - the refresh-token interceptor mints a new pair when the access token has expired
//
// Requests created before such a swap are still on the wire with the previous token. The server
// rejects them with 401 -- correctly -- but that is the answer to a question asked with an old
// key, and says nothing about the token the app holds now. Logging out on it destroys a valid
// session: $auth.logout() clears secure storage, so the app not only bounces to the login screen
// but comes back logged out on the next launch too.
//
// The guard is deliberately narrow. A 401 for the CURRENT token still logs out, so a genuinely
// revoked session is unaffected -- the very next request carries that token and matches.
//
// SHARING ROUTES ARE NOT AFFECTED, even though they look like they should be: on a sharing page
// set-headers.js swaps the session token for the URL's sharing-auth-token (client/ only), so the
// header can never equal $auth.accessToken and every 401 there would count as "stale" here. It
// never comes up -- the router guard returns early for the sharing layout and never fetches a
// user, so $auth.loggedIn stays false and the interceptor's 401 branch, the only caller of this
// predicate, is not reached. Written down because it reads like a behaviour change and is not one.
const readAuthorizationHeader = (headers) => {
  if (!headers) {
    return undefined
  }

  // axios v1 hands the interceptor an AxiosHeaders instance; a plain object survives too
  if (typeof headers.get === 'function') {
    return headers.get('Authorization')
  }

  return headers.Authorization ?? headers.authorization
}

export default ({
  requestHeaders,
  accessToken,
}) => {
  const sentWith = readAuthorizationHeader(requestHeaders)

  // No token on the request, or no session to compare against: nothing to tell apart, so leave
  // the 401 to the normal logout path.
  if (!sentWith || !accessToken) {
    return false
  }

  return sentWith !== `Bearer ${accessToken}`
}
